export enum UserRole {
  ADMIN = 'admin',
  EXECUTOR = 'executor',
  VIEWER = 'viewer',
  ACC_MANAGER = 'acc_manager',
  BUYER = 'buyer',
}

export interface User {
  id: number;
  email: string;
  role: UserRole;
  buyer_id?: string;
  segment_ids?: number[];
  created_at: string;
  updated_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  role?: UserRole;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

export interface ResetPasswordRequest {
  email: string;
}

export interface AuthTokens {
  access_token: string;
  refresh_token?: string;
  expires_at: number;
}

export interface AuthResponse {
  user: User;
  tokens: AuthTokens;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface SetSegmentsRequest {
  segment_ids: number[];
}

export const UserPermissions = {
  canManageUsers: (role?: UserRole) => role === UserRole.ADMIN,

  canManagePush: (role?: UserRole) =>
    role ? [UserRole.ADMIN, UserRole.EXECUTOR].includes(role) : false,

  canViewStats: (role?: UserRole) => !!role,

  canManageApplications: (role?: UserRole) =>
    role ? [UserRole.ADMIN, UserRole.EXECUTOR].includes(role) : false,

  canDeleteApplications: (role?: UserRole) => role === UserRole.ADMIN,

  isAccManager: (role?: UserRole) => role === UserRole.ACC_MANAGER,

  isBuyer: (role?: UserRole) => role === UserRole.BUYER,

  canViewPushHistory: (role?: UserRole) =>
    role
      ? [UserRole.ADMIN, UserRole.EXECUTOR, UserRole.VIEWER, UserRole.ACC_MANAGER, UserRole.BUYER].includes(role)
      : false,

  canAccess: (currentRole?: UserRole, requiredRole?: UserRole) => {
    if (!currentRole) return false;
    if (!requiredRole) return true;

    if (currentRole === UserRole.ACC_MANAGER) {
      return requiredRole === UserRole.ACC_MANAGER;
    }

    if (currentRole === UserRole.BUYER) {
      return requiredRole === UserRole.BUYER;
    }

    const roleHierarchy: Record<UserRole, number> = {
      [UserRole.ADMIN]: 3,
      [UserRole.EXECUTOR]: 2,
      [UserRole.VIEWER]: 1,
      [UserRole.ACC_MANAGER]: 0,
      [UserRole.BUYER]: 0,
    };

    return roleHierarchy[currentRole] >= roleHierarchy[requiredRole];
  },
};